import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { AgentClaudeSettingsEditor } from "../components/AgentClaudeSettingsEditor.tsx";
import { AgentDetailCard } from "../components/AgentDetailCard.tsx";
import { AgentRuntimeEditor } from "../components/AgentRuntimeEditor.tsx";
import { AgentSkillsEditor } from "../components/AgentSkillsEditor.tsx";
import { AgentToolsSummary } from "../components/AgentToolsSummary.tsx";
import { ReopenTaskBox } from "../components/ReopenTaskBox.tsx";
import {
	api,
	ApiError,
	type AgentDirectoryEntry,
} from "../lib/api.ts";

/**
 * Per-agent profile page — everything an owner needs for one project-agent in
 * one place: live status, run command, prompt + AGENT.md editing via GitHub PR.
 *
 * Loaded at /agents/:key. Sibling agents (parent/child) link from each other
 * via the dashboard /agents directory or the business profile.
 */
export function AgentProfile() {
	const { key } = useParams<{ key: string }>();
	const [agent, setAgent] = useState<AgentDirectoryEntry | null>(null);
	const [claudeSettings, setClaudeSettings] = useState<Record<string, unknown> | null>(null);
	const [githubEditEnabled, setGithubEditEnabled] = useState(false);
	const [deploySha, setDeploySha] = useState<string | undefined>(undefined);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!key) return;
		api
			.getAgent(key)
			.then((res) => {
				setAgent(res.agent);
				setClaudeSettings(res.claudeSettings);
				setGithubEditEnabled(res.githubEditEnabled);
			})
			.catch((err) => {
				if (err instanceof ApiError && err.status === 404) {
					setError(`Agent "${key}" not found.`);
				} else {
					setError((err as Error).message);
				}
			});
	}, [key]);

	useEffect(() => {
		// Worker deploy SHA — read from any /status response (it's a Worker-wide
		// constant, not per-agent). One-shot on mount; no polling.
		api
			.listAllAgentStatuses()
			.then((r) => setDeploySha(r.deploySha))
			.catch(() => undefined);
	}, []);

	if (error) {
		return (
			<div className="px-6 py-8">
				<Link to="/agents" className="text-sm text-zinc-500 hover:text-zinc-300">
					← back to agents
				</Link>
				<div className="mt-4 p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300">
					{error}
				</div>
			</div>
		);
	}

	if (!agent) {
		return <div className="px-6 py-8 text-zinc-500">Loading…</div>;
	}

	return (
		<div className="px-6 py-8 max-w-4xl mx-auto">
			<Link to="/agents" className="text-sm text-zinc-500 hover:text-zinc-300">
				← back to agents
			</Link>

			<div className="mt-4 mb-6">
				<h1 className="text-xl font-semibold">{agent.displayName}</h1>
				<div className="text-sm text-zinc-500 mt-1 font-mono">{agent.key}</div>
				<div className="text-sm text-zinc-500 mt-2">
					Serves{" "}
					<Link
						to={`/businesses/${agent.businessId}`}
						className="text-zinc-300 hover:text-zinc-100 underline-offset-2 hover:underline"
					>
						{agent.businessName}
					</Link>
					{agent.parent && (
						<>
							{" · "}
							sub-agent of{" "}
							<Link
								to={`/agents/${agent.parent}`}
								className="text-zinc-300 hover:text-zinc-100 underline-offset-2 hover:underline"
							>
								{agent.parent}
							</Link>
						</>
					)}
				</div>
			</div>

			<AgentDetailCard
				businessId={agent.businessId}
				agent={agent}
				editEnabled={githubEditEnabled}
				deploySha={deploySha}
			/>

			<AgentSkillsEditor agentKey={agent.key} initialSkills={agent.skills ?? []} />

			<AgentToolsSummary agentKey={agent.key} />

			<AgentRuntimeEditor
				agentKey={agent.key}
				initialRuntime={agent.runtime}
				disabled={!githubEditEnabled}
			/>

			<AgentClaudeSettingsEditor agentKey={agent.key} initialSettings={claudeSettings} />

			<ReopenTaskBox agentKey={agent.key} />

			{!githubEditEnabled && (
				<div className="mt-4 text-xs text-zinc-500 italic">
					GitHub PR-flow not configured on this Worker (missing GITHUB_TOKEN secret). The prompt
					and AGENT.md viewers are read-only.
				</div>
			)}
		</div>
	);
}
